/* =========================================================================
 * IS THE CHAMPION A PERSON OR AN ORGANISATION?
 *
 * Brian ran the USEF/EVUSA Vaulting record — champion **Danica Rinard** — and
 * the customer panel answered:
 *
 *   "No company in HubSpot resembles 'Danica Rinard'."
 *
 * That sentence is true and completely misleading. It is rendered in the style
 * reserved for a TAM GAP — "nobody has this customer yet, go get them" — when
 * the real finding is a category error: we searched the company object for a
 * human being. No amount of searching companies will ever find her, and every
 * individual champion SR ever crowns will produce the same false gap. In an
 * individual sport that is not an edge case, it is the norm.
 *
 * ⚠️ WHY THIS IS A SEPARATE, PURE MODULE.
 *
 * Because it is a GUESS about a name, and a guess that steers a rep toward the
 * wrong HubSpot object needs to be testable on its own and wrong in a chosen
 * direction. It is imported by verify-hunt.mjs and driven with real champion
 * names off the board.
 *
 * ⚠️ IT HAS THREE ANSWERS, AND THE THIRD IS THE IMPORTANT ONE.
 *
 * `organisation` and `person` are only returned where the evidence is real: an
 * institution word settles it one way, an individual sport settles it the
 * other. Everything left over is `unknown` — and it is a large, legitimate
 * group, because "Cape Catfish" (a ball club), "Ben Davis" (a high school) and
 * "Danica Rinard" (a vaulter) are the same shape as each other.
 *
 * An earlier version had two answers and forced that group to `person`, which
 * would have pointed a rep at a contact search for a baseball team. Guessing
 * costs a wrong route; `unknown` costs one extra glance at a panel that shows
 * both. That is the whole trade, and it is not close.
 *
 * It NEVER decides anything on its own. Nothing here writes, and nothing here
 * removes a route — a champion classed as a person still gets the company
 * search offered, because the customer for an individual champion is very
 * often the club they compete for. All it does is stop a category error being
 * reported as a gap, and put the other doors on screen.
 * ========================================================================= */

const S = (v) => (v == null ? '' : String(v)).trim()

/* A word that settles it: no human is called any of these. Deliberately about
 * institutions and squads rather than mascots — a mascot list is unbounded and
 * would be wrong forever, and it is not needed, because these words appear in
 * essentially every organisation name SR holds. */
const ORG_WORDS = /\b(school|schools|high|hs|academy|university|univ|college|institute|club|team|athletics|athletic|conference|district|county|city|state|national|association|federation|society|league|program|corps|band|chapter|fraternity|sorority|department|precinct|company|inc|llc|ltd|foundation|center|centre|centre|gym|gymnastics|allstars|all-stars|elite|united|fc|sc|cc|ymca|university's)\b/i

/* Punctuation and shapes that only organisations carry. */
const ORG_SHAPE = /[0-9&/(),]|\b(of|the|and|at)\b/i

/* Suffixes a person's name may legitimately carry. Stripped before counting
 * words so "Danica Rinard Jr." is still two names. */
const SUFFIX = /\b(jr|sr|ii|iii|iv|v)\.?$/i

/**
 * championKind(name, { sport }) → 'person' | 'organisation' | 'unknown'
 *
 * `sport` breaks the tie on a name-shaped string, and only there. It can never
 * turn a name containing an org word into a person — a vaulting CLUB is still
 * a club, and it is the org word that says so.
 */
export function championKind(name, { sport = '' } = {}) {
  const n = S(name)
  if (!n) return 'unknown'
  if (ORG_WORDS.test(n)) return 'organisation'
  if (ORG_SHAPE.test(n)) return 'organisation'

  const words = n.replace(SUFFIX, '').trim().split(/\s+/).filter(Boolean)
  if (words.length < 2 || words.length > 4) return 'organisation'

  /* Every word initial-capped and none SHOUTED. An all-caps token is almost
   * always an acronym, i.e. an organisation. Internal capitals are normal in
   * real surnames — McKinney, MacLeod, O'Brien, Brind'Amour — and rejecting
   * them classed a real champion as an organisation on the first live test. */
  const looksNamely = words.every((w) => /^[A-Z][a-zA-Z'’-]*[a-z]$/.test(w))
  if (!looksNamely) return 'organisation'

  /* ⚠️ HERE THE STRING RUNS OUT OF INFORMATION, AND SAYING SO IS THE POINT.
   *
   * "Cape Catfish" is a baseball team. "Ben Davis" is a high school. "Danica
   * Rinard" is a vaulter. All three are two initial-capped words with no org
   * word in them — they are IDENTICAL in shape, and any rule that splits them
   * is guessing about mascots and place names, which is unbounded and would be
   * wrong forever.
   *
   * The sport is the signal that actually carries the answer, so it decides:
   * an individual sport crowns a human. Anything else returns `unknown`, and
   * the panel then offers BOTH routes instead of picking one and being
   * confidently wrong — which costs a rep one extra glance and never sends
   * them to the wrong object. */
  if (INDIVIDUAL_SPORT.test(S(sport))) return 'person'
  return 'unknown'
}

/* Sports whose champion is a HUMAN by construction. Used only to break the tie
 * on a name-shaped string — never to override an org word. */
export const INDIVIDUAL_SPORT =
  /equestrian|vault|dressage|golf|tennis|wrestl|swim|dive|track|cross country|gymnast|skat|ski|rodeo|shoot|archery|bowl|fenc|judo|karate|taekwondo|boxing|cycling|triathlon|rowing|weightlift|powerlift/i

/**
 * WHO THE CUSTOMER MIGHT BE when the champion is a person.
 *
 * ⚠️ THIS DELIBERATELY DOES NOT PICK ONE. For an individual national champion
 * SR's buyer is genuinely one of three, it varies by sport and by family, and
 * the rep on the record knows which. Guessing here would send outreach to the
 * wrong party while looking authoritative — so the three are named, in the
 * order they are most often right, and a human chooses.
 */
export const PERSON_CUSTOMERS = [
  ['club', 'The club or team they compete for', 'Usually the account SR sells through — they order for every athlete they crown, year after year.'],
  ['athlete', 'The athlete themselves', 'A contact, not a company. Right for an adult competitor buying their own ring.'],
  ['family', 'A parent or guardian', 'Also a contact. Right for a junior competitor — and the parent is who actually pays.'],
]
